import React, { useEffect, useState } from "react";
import { useAuth, API } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { TrendingUp, Mail, Copy } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { InvitationsList, NominationsList, UsersList } from "@/components/admin/AdminLists";

export default function AdminPortal() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", message: "", validity_days: 30 });
  const [sending, setSending] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState("");

  const fetchData = async () => {
    try {
      const [sRes, iRes, nRes, uRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: "include" }),
        fetch(`${API}/admin/invitations`, { credentials: "include" }),
        fetch(`${API}/admin/nominations`, { credentials: "include" }),
        fetch(`${API}/admin/users`, { credentials: "include" }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (iRes.ok) setInvitations(await iRes.json());
      if (nRes.ok) setNominations(await nRes.json());
      if (uRes.ok) { const d = await uRes.json(); setUsers(d.users || []); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const sendInvitation = async () => {
    if (!inviteForm.email || !inviteForm.name) { toast.error("Name and email are required"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/invitations`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(inviteForm),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      const data = await res.json();
      const fullLink = `${window.location.origin}${data.invitation_link}`;
      setLastInviteLink(fullLink);
      setInvitations(i => [data, ...i]);
      if (data.email_sent) {
        toast.success(`Invitation emailed to ${data.email}`);
      } else {
        toast.success("Invitation created! Email not configured — copy the link to share.");
      }
      setInviteForm({ email: "", name: "", message: "", validity_days: 30 });
    } catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/accept-invitation/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invitation link copied!");
  };

  const resendInvitation = async (invitationId) => {
    try {
      const res = await fetch(`${API}/admin/invitations/${invitationId}/resend`, { method: "PUT", credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      toast.success(data.email_sent ? "Invitation renewed and re-emailed" : "Invitation renewed — copy the new link to share");
      fetchData();
    } catch { toast.error("Failed to resend"); }
  };

  const convertNomination = async (nominationId) => {
    try {
      const res = await fetch(`${API}/admin/nominations/${nominationId}/invite`, { method: "PUT", credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      toast.success(data.email_sent ? "Nominee invited and emailed!" : "Invitation created from nomination — copy the link to share");
      fetchData();
    } catch { toast.error("Failed to convert nomination"); }
  };

  const updateUserRole = async (userId, role) => {
    try {
      await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      setUsers(u => u.map(usr => usr.user_id === userId ? { ...usr, role } : usr));
      toast.success("Role updated");
    } catch { toast.error("Failed"); }
  };

  const seedDemo = async () => {
    setSeedingDemo(true);
    try {
      const res = await fetch(`${API}/admin/seed`, { method: "POST", credentials: "include" });
      const data = await res.json();
      toast.success(data.message);
      fetchData();
    } catch { toast.error("Failed to seed demo data"); }
    finally { setSeedingDemo(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF8]"><Navbar />
      <div className="pt-24 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
            <div>
              <p className="section-label mb-1">Admin Portal</p>
              <h1 className="text-3xl font-normal text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Platform Management
              </h1>
            </div>
            <div className="flex gap-3">
              <button onClick={seedDemo} disabled={seedingDemo} className="btn-secondary text-sm flex items-center gap-2" data-testid="seed-demo-btn">
                <TrendingUp size={14} /> {seedingDemo ? "Seeding..." : "Seed Demo Data"}
              </button>
              <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogTrigger asChild>
                  <button className="btn-primary text-sm flex items-center gap-2" data-testid="send-invitation-btn">
                    <Mail size={14} /> Send Invitation
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-white border border-[#E4E4E1] rounded-sm">
                  <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Invite a Council Member</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div>
                      <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">Full Name*</label>
                      <input value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full border border-[#E4E4E1] text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#0F172A]" data-testid="invite-name-input" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">Email*</label>
                      <input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full border border-[#E4E4E1] text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#0F172A]" data-testid="invite-email-input" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-1">Personal Message</label>
                      <textarea rows={3} value={inviteForm.message} onChange={e => setInviteForm(f => ({ ...f, message: e.target.value }))}
                        className="w-full border border-[#E4E4E1] text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#0F172A] resize-none" />
                    </div>
                    <button onClick={sendInvitation} disabled={sending} className="w-full btn-primary text-sm py-2.5" data-testid="create-invitation-btn">
                      {sending ? "Creating..." : "Create Invitation"}
                    </button>
                    {lastInviteLink && (
                      <div className="bg-[#F4F4F1] rounded-sm p-3">
                        <p className="text-xs text-[#52525B] mb-2 font-semibold">Share this link:</p>
                        <div className="flex gap-2">
                          <input readOnly value={lastInviteLink} className="flex-1 text-xs border border-[#E4E4E1] px-2 py-1 rounded-sm bg-white" />
                          <button onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success("Copied!"); }} className="text-xs btn-primary px-3 py-1">
                            <Copy size={12} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Council Members", value: stats?.council_members || 0 },
              { label: "Explorers", value: stats?.explorers || 0 },
              { label: "Total Bookings", value: stats?.total_bookings || 0 },
              { label: "Total Revenue", value: `₹${(stats?.total_revenue || 0).toLocaleString("en-IN")}` },
            ].map((s) => (
              <div key={s.label} className="card-flat text-center">
                <p className="text-2xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                <p className="text-xs text-[#52525B]">{s.label}</p>
              </div>
            ))}
          </div>

          <Tabs defaultValue="invitations">
            <TabsList className="bg-[#F4F4F1] border border-[#E4E4E1] mb-5">
              <TabsTrigger value="invitations" data-testid="admin-tab-invitations">Invitations ({invitations.length})</TabsTrigger>
              <TabsTrigger value="nominations" data-testid="admin-tab-nominations">Nominations ({nominations.length})</TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="invitations">
              <InvitationsList invitations={invitations} onCopyLink={copyLink} onResend={resendInvitation} />
            </TabsContent>

            <TabsContent value="nominations">
              <NominationsList nominations={nominations} onConvert={convertNomination} />
            </TabsContent>

            <TabsContent value="users">
              <UsersList users={users} currentUserId={user.user_id} onRoleChange={updateUserRole} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
